import Grid from "@mui/material/Grid";
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import MenuIcon from '@mui/icons-material/Menu';
import {
    AdaptRecordingMode, formatToReadableLargeTitle, formatToReadableTitle,
    GridVideoLayout,
    LibrettoRecordingMode, LibrettoStudioNameStyle, LibrettoStudioTheme,
    RecordingModeString,
    RefreshTokenAndRetry,
    StandardVideoResolution, StudioNameStyleString, TypeOfTrack
} from "../../utils/utils";
import {useTheme} from "@mui/material/styles";
import {collection, doc, onSnapshot, query, updateDoc, where} from "firebase/firestore";
import {db} from "../../Firebase";
import {
    kRoomStateCollectionName,
    kTracksCollectionName,
    RoomName,
    RoomNotRecording, TrackAssetId, TrackType
} from "../../utils/CollectionConstants";
import {CLIENT_URL, FetchContext} from "../../context/FetchContext";
import {AuthContext} from "../../context/AuthContext";
import {LiveKitRoom, usePersistentUserChoices} from "@livekit/components-react";
import {
    NewStudioVideoConference,
    NewStudioVideoConferenceVideoRecording,
    StudioVideoConference
} from "../../components/LibrettoConference";
import Soundboard from "../Studio/Soundboard";
import {Button, ButtonBase, DialogContent, Typography} from "@mui/material";
import {NewCustomControlBar} from "./NewCustomControlBar";
import Box from "@mui/material/Box";
import {InviteIcon} from "./SettingsIcon";
import HomeIcon from "@mui/icons-material/Home";
import TextField from "@mui/material/TextField";
import useMediaQuery from "@mui/material/useMediaQuery";
import SettingsMenu from "./SettingsMenu";
import Dialog from "@mui/material/Dialog";
import {NewInviteParticipantCard} from "./NewInviteParticipantCard";
import {HostRecordingStoppedOverlay, UploadStatusIndicator} from "./RecordingStoppedOverlay";
import InfoIcon from '@mui/icons-material/Info';
import RecordingGuidelinesCard from "./NewRecordingGuidelines";
import mixpanel from "mixpanel-browser";
import {useConfirmDialog} from "../NewHome/DeleteConfirmation";
import NewRecordButton from "./NewRecordButton";
import {HostLeaveButton} from "../NewGuestStudio/CustomLeaveButton";
import MediaBoard from "./MediaBoard";
import {MediaButton, NewCameraButton, NewMicrophoneButton, NewShareScreenButton, SimpleAudioButton, SimpleScreenShareButton, SimpleVideoButton, RecordButtonWithModeSelector, LeaveCallButton} from "./ControlBarButtons";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import SignInUserIntercomComponent from "../../components/IntercomComponent";
import {NewBackgroundMenu} from "./BackgroundMenu";
import RecordingModeToggle from "./RecordingModeToggle";
import CloseIcon from "@mui/icons-material/Close";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import ContextMenu from "../../librettoEditor/components/ContextMenu/ContextMenu";
import V2StudioSidebar, {StudioSidebarItems} from "./V2StudioSidebar";
import StudioPeopleSidebar from "./StudioPeopleSidebar";
import StudioChatSidebar from "./StudioChatSidebar";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import StudioInviteSidebar from "./StudioInviteSidebar";
import GuestAdmissionModal from "./GuestAdmissionModal";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { trackSessionEvent } from "../../analytics/sessionEvents";
import { ensurePlaceholderMicCamTracksPublished } from "./livekitPlaceholderTracks";
import { RoomEvent } from "livekit-client";
import TeleprompterWindow from "../../components/Teleprompter/TeleprompterWindow";
import { updateParticipantStatus, mapLiveKitConnectionQuality } from "./ParticipantStatusService";
import {
    getPreferredAudioInputDeviceId,
    getPreferredAudioOutputDeviceId,
    getPreferredVideoInputDeviceId,
    setPreferredAudioInputDeviceId,
    setPreferredAudioOutputDeviceId,
    setPreferredVideoInputDeviceId,
} from "../../utils/mediaDevicePreferences";

const serverUrl = 'wss://librettobeta-htow58t4.livekit.cloud'
const STUDIO_RECORDING_COUNTDOWN_SECONDS = 3;
const STUDIO_RECORDING_COUNTDOWN_MS = STUDIO_RECORDING_COUNTDOWN_SECONDS * 1000;

// Back button component styled like V2Editor
const V2EditorBackButton = ({handleClick, disabled, theme}) => {
    const color = theme === LibrettoStudioTheme.Light ? "var(--app-icon-primary)" : "var(--app-text-primary)";
    return (
        <Tooltip title="Back to projects">
            <IconButton onClick={handleClick} disabled={disabled} sx={{
                color: color,
                fontSize: '1.2rem',
                '& .MuiSvgIcon-root': {fontSize: '1.4rem'}
            }}>
                <HomeIcon/>
            </IconButton>
        </Tooltip>
    )
}

// Invite button styled like V2ExportButton
const StudioInviteButton = ({onClick, disabled}) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const activeBackground = isDarkMode ? "#2B6BFD" : "#1a1a1a";
    const activeHoverBackground = isDarkMode ? "#245DDB" : "#111827";
    const iconTextColor = disabled ? "var(--app-text-subtle)" : "#FFFFFF";
    const buttonStyle = {
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        height: "30px",
        minWidth: "70px",
        padding: "0.75rem 1rem",
        border: disabled ? "1px solid var(--app-border-subtle)" : "1px solid var(--app-border-muted)",
        borderRadius: "10px",
        backgroundColor: disabled ? "var(--app-border-subtle)" : activeBackground,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "border 0.2s ease, background-color 0.2s ease",
        fontSize: "0.95rem",
        opacity: !disabled ? 1 : 0.6,
        "&:hover": {
            border: disabled ? "1px solid var(--app-border-subtle)" : "1px solid var(--app-border-muted)",
            backgroundColor: disabled ? "var(--app-border-subtle)" : activeHoverBackground,
            opacity: !disabled ? 0.9 : 0.6,
        },
        justifyContent: "center",
    };

    return (
        <Box sx={buttonStyle} onClick={disabled ? undefined : onClick}>
            <PersonAddOutlinedIcon sx={{color: iconTextColor, fontSize: "1rem"}} />
            <Box sx={{color: iconTextColor, fontSize: "0.8rem"}}>Invite</Box>
        </Box>
    );
};

const NewStudio = ({
    userChoices,
    projectId,
    initialAssetId,
    userFriendlyStudioName,
    isSoloRecording,
    screenShareInitiallyEnabled = false,
    studioOrigin,
    originCompositionId,
}) => {

    const theme = useTheme();
    const isDarkUi = theme.palette.mode === "dark";

    const isTooSmall = useMediaQuery(theme.breakpoints.down('smd'));

    const announcementBarStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '18px',
        backgroundColor: "var(--app-text-primary)",
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999, // Ensure it's above other elements
        overflow: 'hidden',
        transition: 'height 0.3s ease',
        '&:hover': {
            height: '20px', // Expands on hover for better readability
        }
    };

    const announcementTextStyle = {
        color: 'var(--app-bg-surface)',
        fontSize: '13px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    };

    const studioThemeToBackgroundColor = (themeArg) => {
        if (themeArg === LibrettoStudioTheme.Libretto) return "#2B6BBD";
        if (themeArg === LibrettoStudioTheme.Dark || isDarkUi) return "var(--app-bg-canvas)";
        return "var(--app-bg-canvas)";
    }


    const pageStyle = (theme, showAnnouncement) => {

        let backgroundColor = studioThemeToBackgroundColor(theme);

        return ({
            paddingTop: "12px",
            height: "100%",
            width: "100%",
            // Create a gradient background based on color in the backgroundColor variable. Use radial gradient for the top and linear gradient for the bottom.
            background: `radial-gradient(circle at top, ${backgroundColor} 0%, ${backgroundColor} 50%, ${backgroundColor} 100%), linear-gradient(180deg, ${backgroundColor} 0%, ${backgroundColor} 50%, ${backgroundColor} 100%)`,
        })
    }

    const [backgroundImage, setBackgroundImage] = useState({isBlur: false, imageUrl: null});

    const isXl = useMediaQuery(theme.breakpoints.up('xl'));
    const isLg = useMediaQuery(theme.breakpoints.up('lg'));
    const isMd = useMediaQuery(theme.breakpoints.up('md'));
    const isSmd = useMediaQuery(theme.breakpoints.up('smd'));

    const controlBarHeight = isXl ? "93px" : (isLg ? "75px" : "66px");

    const titleAreaWidth = "250px";

    const [videoAreaFullScreen, setVideoAreaFullScreen] = useState(false);

    // Caculated as total minus the area for the top and bottom control bars plus margins
    const videoHeight = videoAreaFullScreen ? "100vh" : "calc(100vh - 240px)";

    const videoWidth = videoAreaFullScreen ? "100vw" : "calc((100vh - 240px) * 16 / 9)";

    const videoDisplayStyle = (videoAreaFullScreen) => {
        return ({
            height: videoHeight,
            width: videoWidth,
            maxWidth: videoAreaFullScreen ? "100vw" : "90vw",
            borderRadius: "0px",
            background: "transparent",
            border: "none",
            boxShadow: "none",
            padding: "0px",
            boxSizing: "border-box",
            overflow: "hidden",
        })
    }

    const controlBarMarginBottom = isXl ? "5px" : (isLg ? "5px" : "5px");

    const [settingsOpen, setSettingsOpen] = useState(false);
    const [inviteCardOpen, setInviteCardOpen] = useState(false);
    const [showRecordingEndedCard, setShowRecordingEndedCard] = useState(true);

    useEffect(() => {
        const handleKey = (event) => {
            if (event.ctrlKey && event.key === 'f') {
                event.preventDefault(); // Prevent the default F11 behavior
                setVideoAreaFullScreen(!videoAreaFullScreen);
            } else if (event.key === 'Escape' && videoAreaFullScreen) {
                setVideoAreaFullScreen(false);
            }
        };
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('keydown', handleKey);
        };
    }, []);

    const soundBoardStyle = (videoAreaFullScreen) => {
        return ({
            height: "58px",
            marginTop: controlBarMarginBottom,
            paddingLeft: "16px",
            paddingRight: "16px",
            paddingTop: "10px",
            paddingBottom: "8px",
            display: videoAreaFullScreen ? 'none' : 'inline-flex',
            width: "auto",
            borderRadius: "0px",
            backgroundColor: "transparent",
        })
    }

    const smallButtonStyle = {
        display: 'flex',
        height: "34px",
        padding: "8px",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px",
        cursor: 'pointer',
        width: "34px",
        flexDirection: "row",
        borderRadius: "6px",
        border: "1px solid var(--app-border-subtle)",
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        backgroundColor: "var(--app-bg-surface)",
        '&:hover': {
            backgroundColor: 'var(--app-bg-subtle)',
            borderColor: 'var(--app-border-muted)',
        }
    }

    const inviteButtonTextStyle = {
        fontFamily: "Inter",
        fontSize: "14px",
        color: "white",
        fontStyle: "normal",
        fontWeight: 500,
        lineHeight: "20px",
        letterSpacing: "0.14px",
    }

    const titleStyle = (theme) => {
        return ({
            fontFamily: "Inter",
            color: theme === LibrettoStudioTheme.Light ? "var(--app-text-primary)" : "var(--app-text-primary)",
            fontSize: "17px",
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: "24px",
            letterSpacing: "0.13px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
        })
    }


    const [cloudEgressStarted, setCloudEgressStarted] = useState(false);   // This means cloud egress has been started by the backend.
    const [recordingInProgress, setRecordingInProgress] = useState(false); // This is the client UI state to show flashing red and timer.
    const [countdown, setCountdown] = useState(null);
    const [studioData, setStudioData] = useState({
        token: '',
        roomName: '',
        userFriendlyRoomName: '',
        userPlan: null,
        hostIdentity: ''
    });
    const [inviteLink, setInviteLink] = useState('');
    const [soundboardToken, setSoundboardToken] = useState('');
    const [showStoppedCard, setShowStoppedCard] = useState(false);
    const navigate = useNavigate();
    const navigateToHome = () => navigate("/dashboard");
    const assetIdRef = useRef(null);
    const projectIdRef = useRef(projectId); // May be updated by /record response
    const [recordingStarting, setRecordingStarting] = useState(false);
    const [localRecordingInProgress, setLocalRecordingInProgress] = useState(false)
    const [participantsLocalRecordingDone, setParticipantsLocalRecordingDone] = useState(false);
    const [selectedVideoResolution, setSelectedVideoResolution] = useState(StandardVideoResolution);
    const [selectedVideoLayout, setSelectedVideoLayout] = useState(GridVideoLayout);
    const [noiseCancellationEnabled, setNoiseCancellationEnabled] = useState(false);
    const [localRecordingEnabled, setLocalRecordingEnabled] = useState(false);
    const [soundboardRecordingEnabled, setSoundboardRecordingEnabled] = useState(false);
    const [invitedEmails, setInvitedEmails] = useState([]);
    const [videoMirrored, setVideoMirrored] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState("en");
    const [selectedBrandLogo, setSelectedBrandLogo] = useState({assetId: null, url: null});
    const [recordingMode, setRecordingMode] = useState(LibrettoRecordingMode.Audio);
    const [studioTheme, setStudioTheme] = useState(LibrettoStudioTheme.Light);
    const [studioNameStyle, setStudioNameStyle] = useState(LibrettoStudioNameStyle.Clean);
    const [hostVideoResolution, setHostVideoResolution] = useState('1080p');

    const [uploadProgress, setUploadProgress] = useState(0);
    const [screenShareUploadProgress, setScreenShareUploadProgress] = useState(null);

    const effectiveUploadProgress = useMemo(() => {
        const parts = [uploadProgress];
        if (typeof screenShareUploadProgress === 'number') parts.push(screenShareUploadProgress);
        const denom = parts.length;
        return denom > 0 ? parts.reduce((a, b) => a + b, 0) / denom : 0;
    }, [uploadProgress, screenShareUploadProgress]);

    const [recordingPerformed, setRecordingPerformed] = useState(false);

    const [soundBoardRecordingDone, setSoundBoardRecordingDone] = useState(false);
    
    // Editable title state
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleValue, setTitleValue] = useState(userChoices.title || 'My podcast episode');
    const [localRecordingStopped, setLocalRecordingStopped] = useState(false);

    // Set page title
    useEffect(() => {
        document.title = titleValue || "Recording Studio";
    }, [titleValue]);

    const readableRecordingMode = (mode) => {
        return mode === LibrettoRecordingMode.Audio ? "audio-only recording" : "video recording";
    }

    const readableVideoResolution = (resolution) => {
        return resolution === StandardVideoResolution ? "Standard" : "High-definition";
    }

    const [showAnnouncement, setShowAnnouncement] = useState(false); // State for the announcement bar [true/false
    const [announcement, setAnnouncement] = useState("Welcome to your studio. View recording guidelines "); // State for the announcement message

    const [recordingDisabled, setRecordingDisabled] = useState(false); // only used for solo sessions to disable button when all input (cam, mic, screen) is disabled.
    const [teleprompterOpen, setTeleprompterOpen] = useState(false);

    const nowMs = () => {
        try {
            if (typeof performance !== 'undefined' && typeof performance.now === 'function') return performance.now();
        } catch {}
        return Date.now();
    };

    const recordingStartMsRef = useRef(null);
    const recordingStopMsRef = useRef(null);
    useEffect(() => {
        if (recordingInProgress) {
            recordingStartMsRef.current = nowMs();
            recordingStopMsRef.current = null;
            return;
        }
        if (recordingStartMsRef.current != null && recordingStopMsRef.current == null) {
            recordingStopMsRef.current = nowMs();
        }
    }, [recordingInProgress]);

    const getRecordingElapsedMs = useCallback(() => {
        const startedAt = recordingStartMsRef.current;
        if (startedAt == null) return 0;
        const endedAt = recordingStopMsRef.current;
        const endTime = endedAt != null ? endedAt : nowMs();
        return Math.max(0, endTime - startedAt);
    }, []);

    useEffect(() => {
        setAnnouncement("Welcome to your studio. View recording guidelines ");
    }, [recordingMode]);

    const handleSetSoundBoardRecordingDone = (value) => {
        console.log("Soundboard recording done: ", value);
        setSoundBoardRecordingDone(value);
    }

    // Refs to track current values for Firestore snapshot handlers (avoiding stale closures)
    const countdownRef = useRef(null);
    const cloudEgressStartedRef = useRef(false);
    const recordingModeRef = useRef(recordingMode);
    const recordingStartsAtEpochMsRef = useRef(null);
    const pendingRecordingStartBoundaryWriteRef = useRef(false);

    const [room, setRoom] = useState(null);

    useEffect(() => {
        if (!room) return;
        let disposed = false;
        const ensure = () => {
            if (disposed) return;
            ensurePlaceholderMicCamTracksPublished(room, {
                expectMicrophoneEnabled: !!userChoices?.audioEnabled,
                expectCameraEnabled: !!userChoices?.videoEnabled,
            }).catch(() => {});
        };
        ensure();
        try {
            room.on?.(RoomEvent.Connected, ensure);
            room.on?.(RoomEvent.Reconnected, ensure);
        } catch {}
        return () => {
            disposed = true;
            try {
                room.off?.(RoomEvent.Connected, ensure);
                room.off?.(RoomEvent.Reconnected, ensure);
            } catch {}
        };
    }, [room]);

    // Report host status to backend (includes screen-share uploads in progress calc).
    useEffect(() => {
        if (studioData.roomName === '' || studioData.token === '' || !room) return;

        const sendStatusUpdate = () => {
            const localParticipant = room.localParticipant;
            if (!localParticipant) return;

            const participantIdentifier = studioData.hostIdentity;
            if (!participantIdentifier) return;

            const quality = mapLiveKitConnectionQuality(localParticipant.connectionQuality);

            updateParticipantStatus(studioData.token, {
                roomName: studioData.roomName,
                participantIdentifier,
                connectionQuality: quality,
                videoResolution: hostVideoResolution,
                // Send upload progress during recording (real-time uploads) and after stopped
                uploadProgress: (recordingInProgress || localRecordingStopped) ? effectiveUploadProgress : undefined,
            }).catch((err) => console.error('Failed to update participant status:', err));
        };

        // Send initial update
        sendStatusUpdate();

        // Send updates every 5 seconds (more frequent for upload progress)
        const intervalId = setInterval(sendStatusUpdate, 5000);

        return () => clearInterval(intervalId);
    }, [studioData.roomName, studioData.token, studioData.hostIdentity, room, hostVideoResolution, effectiveUploadProgress, localRecordingStopped, recordingInProgress]);

    // Participants from subcollection (for People sidebar)
    const [participants, setParticipants] = useState([]);

    // Derive host upload status from participants array (host now has a participant entry)
    const hostParticipant = participants.find(p => p.isHost);
    const hostUploadCompleted = hostParticipant?.uploadComplete ?? false;
    const hostLocalRecordingError = hostParticipant?.localRecordingError || '';

    // Pending guests waiting to be admitted
    const [pendingGuests, setPendingGuests] = useState([]);
    const [showAdmissionModal, setShowAdmissionModal] = useState(true);
    const [isProcessingAdmission, setIsProcessingAdmission] = useState(false);
    const previousPendingCountRef = useRef(0);

    // Play a pleasant notification sound when new guest requests to join
    const playNotificationSound = () => {
        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            
            // Create a pleasant two-tone ding
            const playTone = (frequency, startTime, duration) => {
                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();
                
                oscillator.connect(gainNode);
                gainNode.connect(audioContext.destination);
                
                oscillator.frequency.value = frequency;
                oscillator.type = 'sine';
                
                // Envelope for smooth attack and decay
                gainNode.gain.setValueAtTime(0, startTime);
                gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.02);
                gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
                
                oscillator.start(startTime);
                oscillator.stop(startTime + duration);
            };
            
            const now = audioContext.currentTime;
            // Two-tone ding (like a doorbell)
            playTone(880, now, 0.15);        // A5
            playTone(1108.73, now + 0.08, 0.2); // C#6
            
            // Clean up after sound finishes
            setTimeout(() => audioContext.close(), 500);
        } catch (e) {
            console.log('Could not play notification sound:', e);
        }
    };

    const [backgroundImageIndex, setBackgroundImageIndex] = useState(null);

    const [mediaBoardOpen, setMediaBoardOpen] = useState(false);

    const highQualityRecordingRef = useRef(null);
    const highQualitySoundboardRecordingRef = useRef(null);

    const shareScreenButtonRef = useRef(null);
    
    // Ref to prevent double join calls in StrictMode
    const hasJoinedRef = useRef(false);

    const startHQRecording = () => {
        if (highQualityRecordingRef.current && !highQualityRecordingRef.current.isHighQualityRecordingActive()) {
            highQualityRecordingRef.current.startRecording();
        }
        if (highQualitySoundboardRecordingRef.current && !highQualitySoundboardRecordingRef.current.isHighQualityRecordingActive()) {
            highQualitySoundboardRecordingRef.current.startRecording();
        }
    }

    const startHQVideoWarmup = (officialStartAtEpochMs) => {
        if (recordingModeRef.current !== LibrettoRecordingMode.Video) return;
        if (highQualityRecordingRef.current && !highQualityRecordingRef.current.isHighQualityRecordingActive()) {
            highQualityRecordingRef.current.startRecording({ officialStartAtEpochMs });
        }
    };

    const reliablyStartRecording = (opts = {}) => {
        const officialStartAtEpochMs = typeof opts.officialStartAtEpochMs === 'number' && Number.isFinite(opts.officialStartAtEpochMs)
            ? Math.round(opts.officialStartAtEpochMs)
            : null;
        if (recordingModeRef.current === LibrettoRecordingMode.Video && officialStartAtEpochMs != null) {
            startHQVideoWarmup(officialStartAtEpochMs);
        } else {
            startHQRecording();
        }
        // Call start recording a couple of times to make sure it starts.
        setTimeout(() => {
            if (recordingModeRef.current === LibrettoRecordingMode.Video && officialStartAtEpochMs != null) {
                startHQVideoWarmup(officialStartAtEpochMs);
            } else {
                startHQRecording();
            }
        }, 300);
        setTimeout(() => {
            if (recordingModeRef.current === LibrettoRecordingMode.Video && officialStartAtEpochMs != null) {
                startHQVideoWarmup(officialStartAtEpochMs);
            } else {
                startHQRecording();
            }
        }, 600);
    }

    const stopHQRecording = () => {
        if (highQualityRecordingRef.current && highQualityRecordingRef.current.isHighQualityRecordingActive()) {
            highQualityRecordingRef.current.stopRecording();
        }
        if (highQualitySoundboardRecordingRef.current && highQualitySoundboardRecordingRef.current.isHighQualityRecordingActive()) {
            highQualitySoundboardRecordingRef.current.stopRecording();
        }
    }

    const reliablyStopRecording = () => {
        stopHQRecording();
        setTimeout(() => {
            stopHQRecording();
        }, 300);
        setTimeout(() => {
            stopHQRecording();
        }, 600);
    }

    // Keep refs in sync with state for use in Firestore snapshot handlers
    useEffect(() => {
        countdownRef.current = countdown;
    }, [countdown]);

    useEffect(() => {
        cloudEgressStartedRef.current = cloudEgressStarted;
    }, [cloudEgressStarted]);

    useEffect(() => {
        recordingModeRef.current = recordingMode;
    }, [recordingMode]);

    const normalizeRecordingStartEpochMs = (value) => {
        return typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : null;
    };

    const getCountdownValueFromRecordingStart = (recordingStartsAtMs) => {
        if (typeof recordingStartsAtMs !== 'number' || !Number.isFinite(recordingStartsAtMs)) {
            return STUDIO_RECORDING_COUNTDOWN_SECONDS;
        }
        const remainingMs = recordingStartsAtMs - Date.now();
        if (remainingMs <= 0) return 0;
        return Math.max(1, Math.ceil(remainingMs / 1000));
    };

    const ensureHostRecordingStartBoundary = useCallback(async (roomName, existingRecordingStartsAtMs) => {
        if (!roomName || recordingModeRef.current !== LibrettoRecordingMode.Video) {
            return normalizeRecordingStartEpochMs(existingRecordingStartsAtMs);
        }
        const normalizedExisting = normalizeRecordingStartEpochMs(existingRecordingStartsAtMs);
        if (normalizedExisting != null) {
            return normalizedExisting;
        }
        if (pendingRecordingStartBoundaryWriteRef.current) {
            return null;
        }

        pendingRecordingStartBoundaryWriteRef.current = true;
        const nextRecordingStartsAtMs = Date.now() + STUDIO_RECORDING_COUNTDOWN_MS;
        try {
            await updateDoc(doc(db, kRoomStateCollectionName, roomName), {
                recordingStartsAtMs: nextRecordingStartsAtMs,
            });
            return nextRecordingStartsAtMs;
        } catch (error) {
            console.error('Error updating recording start boundary for room: ', error);
            return null;
        } finally {
            pendingRecordingStartBoundaryWriteRef.current = false;
        }
    }, []);

    const handleNavigateToRecording = () => {
        const params = new URLSearchParams();
        params.set("openProjectSidebarFiles", "1");

        if (studioOrigin === "editor" && originCompositionId && assetIdRef.current) {
            params.set("studioAutocreate", "1");
            params.set("studioOrigin", "editor");
            params.set("originCompositionId", String(originCompositionId));
            params.set("studioAssetId", String(assetIdRef.current));
        }

        navigate(`/editor/${projectIdRef.current}?${params.toString()}`);
    }

    // When recording is stopped, check the status of all local recording tracks for the assetId to see if all
    // local recordings are in "processing" state.
    useEffect(() => {
        if (!assetIdRef.current) {
            return;
        }

        if (participantsLocalRecordingDone) {
            return;
        }

        const q = query(collection(db, kTracksCollectionName), where(TrackAssetId, "==", assetIdRef.current),
            where(TrackType, "in", [TypeOfTrack.LocalAudioRecording, TypeOfTrack.LocalVideoRecording, TypeOfTrack.SoundboardLocalRecording]));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const tracks = [];
            querySnapshot.forEach((doc) => {
                tracks.push(doc.data());
            });
            const allTracksDone = tracks.every(track => track.status !== "Recording in progress");
            if (allTracksDone) {
                setParticipantsLocalRecordingDone(true);
            }
        }, (error) => {
            // Log Firestore errors but don't try to refresh token - Firestore handles this internally
            // Note: "Failed to obtain primary lease" errors are benign IndexedDB persistence issues
            if (error?.message?.includes('primary lease')) {
                console.warn('Tracks listener: IndexedDB lease contention (benign):', error.code);
            } else {
                console.error('Firestore tracks listener error:', error);
            }
        });

        return () => unsubscribe();
    }, [participantsLocalRecordingDone]);

    // Main state check. This is how recording is started/stopped and other studio settings are updated across clients.
    // NOTE: We use refs (countdownRef, cloudEgressStartedRef) to avoid stale closures in the snapshot handler.
    // We also use functional state updates to only trigger re-renders when values actually change.
    useEffect(() => {
        if (studioData.roomName === '') {
            return;
        }

        const q = query(collection(db, kRoomStateCollectionName), where(RoomName, "==", studioData.roomName));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const newRoomState = [];
            querySnapshot.forEach((doc) => {
                newRoomState.push(doc.data());
            });
            if (newRoomState.length > 0) {
                const data = newRoomState[0];
                
                // Only update state if values have actually changed (prevents unnecessary re-renders)
                setNoiseCancellationEnabled(prev => prev === data.backgroundNoiseCancellationEnabled ? prev : data.backgroundNoiseCancellationEnabled);
                setLocalRecordingEnabled(prev => {
                    const newVal = data.hasOwnProperty("localRecordingEnabled") ? data.localRecordingEnabled : true;
                    return prev === newVal ? prev : newVal;
                });
                setSoundboardRecordingEnabled(prev => {
                    const newVal = data.hasOwnProperty("soundboardRecordingEnabled") ? data.soundboardRecordingEnabled : false;
                    return prev === newVal ? prev : newVal;
                });
                setVideoMirrored(prev => {
                    const newVal = data.hasOwnProperty("videoMirrored") ? data.videoMirrored : false;
                    return prev === newVal ? prev : newVal;
                });
                setInvitedEmails(prev => {
                    const newVal = data.hasOwnProperty("invitedEmails") ? data.invitedEmails : [];
                    // Compare arrays by JSON stringification for simplicity
                    return JSON.stringify(prev) === JSON.stringify(newVal) ? prev : newVal;
                });
                setSelectedVideoResolution(prev => prev === data.videoRecordingQuality ? prev : data.videoRecordingQuality);
                setSelectedVideoLayout(prev => {
                    const newVal = data.videoRecordingLayout ? data.videoRecordingLayout : GridVideoLayout;
                    return prev === newVal ? prev : newVal;
                });
                setSelectedLanguage(prev => prev === data.transcriptionLanguage ? prev : data.transcriptionLanguage);
                const nextRecordingMode = AdaptRecordingMode(data.recordingType);
                recordingModeRef.current = nextRecordingMode;
                setRecordingMode(prev => prev === nextRecordingMode ? prev : nextRecordingMode);
                setStudioTheme(prev => {
                    const newVal = data.studioTheme ? data.studioTheme : LibrettoStudioTheme.Light;
                    return prev === newVal ? prev : newVal;
                });
                setStudioNameStyle(prev => {
                    const newVal = data.studioNameStyle ? data.studioNameStyle : LibrettoStudioNameStyle.Clean;
                    return prev === newVal ? prev : newVal;
                });
                // Note: hostUploadCompleted is now derived from the host's participant entry
                setSelectedBrandLogo(prev => {
                    const newVal = data.studioBrandLogo && data.studioBrandLogo != "" ? data.studioBrandLogo : { assetId: null, url: null };
                    // Compare objects by JSON stringification
                    return JSON.stringify(prev) === JSON.stringify(newVal) ? prev : newVal;
                });
                
                // RoomNotRecording
                if (data.roomState === 0) {
                    setRecordingInProgress(false);
                    setCloudEgressStarted(false);
                    setCountdown(null);
                    setRecordingStarting(false);
                    recordingStartsAtEpochMsRef.current = null;
                    pendingRecordingStartBoundaryWriteRef.current = false;
                    reliablyStopRecording();
                }
                // RoomRecordingStarting - use ref to check current countdown value (avoids stale closure)
                if (data.roomState === 1) {
                    let recordingStartsAtMs = normalizeRecordingStartEpochMs(data.recordingStartsAtMs);
                    const previousRecordingStartsAtMs = recordingStartsAtEpochMsRef.current;
                    const hasRecordingStartBoundaryChanged =
                        recordingStartsAtMs != null && recordingStartsAtMs !== previousRecordingStartsAtMs;
                    if (nextRecordingMode === LibrettoRecordingMode.Video && recordingStartsAtMs == null) {
                        void ensureHostRecordingStartBoundary(studioData.roomName, data.recordingStartsAtMs).then((resolvedRecordingStartsAtMs) => {
                            if (resolvedRecordingStartsAtMs == null) return;
                            recordingStartsAtEpochMsRef.current = resolvedRecordingStartsAtMs;
                            setCountdown(prev => {
                                const nextCountdown = getCountdownValueFromRecordingStart(resolvedRecordingStartsAtMs);
                                return prev === nextCountdown ? prev : nextCountdown;
                            });
                            reliablyStartRecording({ officialStartAtEpochMs: resolvedRecordingStartsAtMs });
                        });
                    }

                    if (recordingStartsAtMs != null) {
                        recordingStartsAtEpochMsRef.current = recordingStartsAtMs;
                    }

                    if (countdownRef.current === null || hasRecordingStartBoundaryChanged) {
                        setRecordingStarting(true);
                        setCountdown(prev => {
                            const nextCountdown = getCountdownValueFromRecordingStart(recordingStartsAtMs);
                            return prev === nextCountdown ? prev : nextCountdown;
                        });
                        setShowAnnouncement(false);

                        if (nextRecordingMode === LibrettoRecordingMode.Video && recordingStartsAtMs != null) {
                            reliablyStartRecording({ officialStartAtEpochMs: recordingStartsAtMs });
                        }
                    }
                }
                // RoomRecordingInProgress - use ref to check current cloudEgressStarted value (avoids stale closure)
                if (data.roomState === 2 && !cloudEgressStartedRef.current) {
                    setCloudEgressStarted(true);
                    reliablyStartRecording();
                }
            }
        }, (error) => {
            // Log Firestore errors - Firestore handles token refresh internally
            // If permission-denied persists, it's likely a security rules issue, not auth
            // Note: "Failed to obtain primary lease" errors are benign IndexedDB persistence issues
            if (error?.message?.includes('primary lease')) {
                console.warn('Firestore room state listener: IndexedDB lease contention (benign):', error.code);
            } else {
                console.error('Firestore room state listener error:', error);
            }
        });

        return () => unsubscribe();
    }, [studioData.roomName, ensureHostRecordingStartBoundary]); // Only depend on roomName, not entire studioData object

    // Subscribe to participants subcollection for People sidebar
    useEffect(() => {
        if (studioData.roomName !== '') {
            const participantsRef = collection(db, kRoomStateCollectionName, studioData.roomName, 'participants');
            const unsubscribe = onSnapshot(participantsRef, (snapshot) => {
                const participantsList = [];
                snapshot.forEach((docSnapshot) => {
                    const data = docSnapshot.data();
                    // Both host and guests now have participant entries
                    // The isHost field distinguishes between them
                        participantsList.push({
                            id: docSnapshot.id, // participantIdentifier
                            name: data.participantName || 'Guest',
                            connectionQuality: data.connectionQuality || 'unknown',
                            videoResolution: data.videoResolution || null,
                            uploadProgress: data.uploadProgress,
                            uploadComplete: data.allUploadsCompleted === true,
                            noiseCancellationEnabled: data.noiseCancellationEnabled ?? false,
                            isHost: data.isHost === true,
                            localTrackId: data.localTrackId || null,  // For local recording trackId delivery
                            screenRecordingLocalTrackId: data.screenRecordingLocalTrackId || null,
                            localRecordingError: data.localRecordingError || '',
                        });

                    // For host: deliver localTrackId via Firestore (fallback when WebRTC metadata fails)
                    if (data.isHost === true && data.localTrackId && highQualityRecordingRef.current) {
                        highQualityRecordingRef.current.onTrackIdReceived?.(data.localTrackId);
                    }
                });
                setParticipants(participantsList);
            }, (error) => {
                // Log Firestore errors - Firestore handles token refresh internally
                // Note: "Failed to obtain primary lease" errors are benign IndexedDB persistence issues
                if (error?.message?.includes('primary lease')) {
                    console.warn('Participants listener: IndexedDB lease contention (benign):', error.code);
                } else {
                    console.error('Error subscribing to participants:', error);
                }
            });
            return () => unsubscribe();
        }
    }, [studioData.roomName]);

    // Subscribe to pending guests waiting to be admitted
    useEffect(() => {
        if (studioData.roomName === '' || isSoloRecording) {
            return;
        }

        const pendingGuestsRef = collection(db, kRoomStateCollectionName, studioData.roomName, 'pendingGuests');
        const unsubscribe = onSnapshot(pendingGuestsRef, (snapshot) => {
            const pendingList = [];
            snapshot.forEach((docSnapshot) => {
                const data = docSnapshot.data();
                // Only show guests with "pending" status
                if (data.status === 'pending') {
                    pendingList.push({
                        pendingId: docSnapshot.id,
                        guestName: data.guestName || 'Guest',
                        requestedAt: data.requestedAt,
                    });
                }
            });
            // Sort by request time (oldest first)
            pendingList.sort((a, b) => {
                const timeA = a.requestedAt?.toMillis?.() || 0;
                const timeB = b.requestedAt?.toMillis?.() || 0;
                return timeA - timeB;
            });
            
            // Play notification sound if new guests arrived
            if (pendingList.length > previousPendingCountRef.current) {
                playNotificationSound();
            }
            previousPendingCountRef.current = pendingList.length;
            
            setPendingGuests(pendingList);
            // Auto-show modal when new guests arrive
            if (pendingList.length > 0) {
                setShowAdmissionModal(true);
            }
        }, (error) => {
            // Note: "Failed to obtain primary lease" errors are benign IndexedDB persistence issues
            if (error?.message?.includes('primary lease')) {
                console.warn('Pending guests listener: IndexedDB lease contention (benign):', error.code);
            } else {
                console.error('Error subscribing to pending guests:', error);
            }
        });

        return () => unsubscribe();
    }, [studioData.roomName, isSoloRecording]);

    // Toggle noise cancellation for a guest participant (direct Firestore write to participants subcollection)
    const handleToggleNoiseCancellation = async (participantId, enabled) => {
        if (studioData.roomName === '' || participantId === 'host') return;
        
        const participantRef = doc(db, kRoomStateCollectionName, studioData.roomName, 'participants', participantId);
        try {
            await updateDoc(participantRef, { noiseCancellationEnabled: enabled });
        } catch (error) {
            console.error('Error updating noise cancellation for participant:', error);
        }
    };

    // Toggle noise cancellation for the host (updates room-level backgroundNoiseCancellationEnabled field)
    const handleToggleHostNoiseCancellation = async (enabled) => {
        if (studioData.roomName === '') return;
        
        const roomRef = doc(db, kRoomStateCollectionName, studioData.roomName);
        try {
            await updateDoc(roomRef, { backgroundNoiseCancellationEnabled: enabled });
            setNoiseCancellationEnabled(enabled); // Optimistically update local state
        } catch (error) {
            console.error('Error updating host noise cancellation:', error);
        }
    };

    // Approve a pending guest
    const handleApproveGuest = async (guest) => {
        if (studioData.roomName === '' || isProcessingAdmission) return;
        
        setIsProcessingAdmission(true);
        try {
            await fetchContext.authAxios.post('/guest/approve', {
                roomName: studioData.roomName,
                pendingId: guest.pendingId,
            }, {
                headers: {
                    Authorization: `Bearer ${auth.getToken()}`,
                }
            });
            // Firestore listener will automatically remove them from pendingGuests
        } catch (error) {
            console.error('Error approving guest:', error);
            if (error.response?.status === 401) {
                await RefreshTokenAndRetry(error, auth, fetchContext);
            }
        } finally {
            setIsProcessingAdmission(false);
        }
    };

    // Deny a pending guest
    const handleDenyGuest = async (guest) => {
        if (studioData.roomName === '' || isProcessingAdmission) return;
        
        setIsProcessingAdmission(true);
        try {
            await fetchContext.authAxios.post('/guest/deny', {
                roomName: studioData.roomName,
                pendingId: guest.pendingId,
            }, {
                headers: {
                    Authorization: `Bearer ${auth.getToken()}`,
                }
            });
            // Firestore listener will automatically update their status
        } catch (error) {
            console.error('Error denying guest:', error);
            if (error.response?.status === 401) {
                await RefreshTokenAndRetry(error, auth, fetchContext);
            }
        } finally {
            setIsProcessingAdmission(false);
        }
    };

    const urlFriendlyUserFriendlyRoomName = ({userFriendlyRoomName}) => {
        return userFriendlyRoomName.replace(/ /g, '-');
    }

    // Contexts
    const fetchContext = useContext(FetchContext);
    const auth = useContext(AuthContext);

    const tokenOptions = useMemo(() => {
        return {
            userInfo: {
                identity: userChoices.username,
                name: userChoices.username,
            },
        };
    }, [userChoices.username]);

    const startRecording = async () => {
        setShowStoppedCard(false);
        try {
            const response = await fetchContext.authAxios.post('/record', {
                projectId: projectId,
                workspaceId: auth.getCurrentWorkspaceId?.(),
                type: RecordingModeString(recordingMode),
                title: recordingTitle,
                hostIdentity: tokenOptions.userInfo.identity,
                livekitToken: studioData.token,
                initialAssetId: initialAssetId,
                isSoloRecording: isSoloRecording,
                isScreenShareEnabled: isScreenShareEnabled,
            }, {
                headers: {
                    Authorization: `Bearer ${auth.getToken()}`,
                }
            });
            mixpanel.track('Recording started', {
                'assetId': response.data.assetId,
                'projectId': response.data.projectId,
                'localRecordingEnabled': localRecordingEnabled,
                'recordingMode': readableRecordingMode(recordingMode),
                'videoResolution': readableVideoResolution(selectedVideoResolution),
                'localRecordingStarted': highQualityRecordingRef.current.isHighQualityRecordingActive(),
            });
            assetIdRef.current = response.data.assetId;
            // Store projectId from response (may be newly created if prop was empty)
            if (response.data.projectId) {
                projectIdRef.current = response.data.projectId;
            }
        } catch (error) {
            console.log("Error starting recording: ", error)
            if (error.response.status === 401) {
                await RefreshTokenAndRetry(error, auth, fetchContext);
            }
        }
    };

    const stopRecording = async () => {
        try {
            await fetchContext.authAxios.post(`/stop`, {
                assetId: assetIdRef.current,
            }, {
                headers: {
                    Authorization: `Bearer ${auth.getToken()}`,
                }
            });
            setRecordingPerformed(true);
        } catch (error) {
            if (error.response.status === 401) {
                await RefreshTokenAndRetry(error, auth, fetchContext);
            }
        }
    };

    // The client UI state.
    useEffect(() => {
        if (cloudEgressStarted && countdown === null) {
            setRecordingInProgress(true);
        }
    }, [cloudEgressStarted, countdown]);

    useEffect(() => {
        setLocalRecordingInProgress(recordingStarting || recordingInProgress);
    }, [recordingStarting, recordingInProgress]);

    // Countdown before recording starts.
    useEffect(() => {
        let intervalId;
        if (countdown > 0) {
            intervalId = setInterval(() => {
                setCountdown((prevCountdown) => prevCountdown - 1);
            }, 1000);
        } else if (countdown === 0) {
            setCountdown(null);
        }

        // Clear interval on unmount or countdown change
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [countdown]);

    // Join studio when component loads. And make sure the room state is recording not started.
    useEffect(() => {
        // Prevent double join in StrictMode
        if (hasJoinedRef.current) {
            console.log("Join studio already called, skipping (StrictMode double-invoke)");
            return;
        }
        hasJoinedRef.current = true;

        const updateRoomState = async ({roomName}) => {
            if (roomName === '') {
                return;
            }

            const entityRef = doc(db, kRoomStateCollectionName, roomName);
            try {
                await updateDoc(entityRef, {
                    roomState: RoomNotRecording,
                    recordingStartsAtMs: null,
                });
            } catch (error) {
                console.error('Error updating recording type for room: ', error);
            }
        }
        const joinStudio = async () => {
            console.log("Join studio called")
            try {
                const {data} = await fetchContext.authAxios.post('/join', {
                    identity: tokenOptions.userInfo.identity,
                }, {
                    headers: {
                        Authorization: `Bearer ${auth.getToken()}`,
                    }
                });
                // When joining a room as a host (the first person in the room), make sure room is in a not recording state.
                updateRoomState({roomName: data.roomName});
                setStudioData({
                    token: data.token,
                    roomName: data.roomName,
                    userFriendlyRoomName: data.userFriendlyRoomName,
                    userPlan: data.userPlan,
                    hostIdentity: data.hostIdentity
                });
                console.log("User plan on join is: ", data.userPlan);
                setInviteLink(guestURL(data.roomName, data.userFriendlyRoomName));
                trackSessionEvent("joined_studio");
            } catch (err) {
                if (err && err.response.status === 401) {
                    await RefreshTokenAndRetry(err, auth, fetchContext);
                }
                if (err.code === "ERR_NETWORK") {
                    console.error("Network error joining studio:", err);
                    setAnnouncement("Network error connecting to Libretto. Please check your connection and refresh the page.");
                    setShowAnnouncement(true);
                }
            }
        }

        joinStudio();
    }, []);

    // Add the soundboard to the studio as a hidden participant when the component loads.
    useEffect(() => {
        if (studioData.token === '' || studioData.roomName === '')
            return;
        const joinSoundboard = async () => {
            try {
                const {data} = await fetchContext.authAxios.post('/soundboard', {
                    identity: "libretto_user_soundboard_" + studioData.roomName,
                }, {
                    headers: {
                        Authorization: `Bearer ${auth.getToken()}`,
                    }
                });
                setSoundboardToken(data.token);
            } catch (err) {
                if (err.code === "ERR_NETWORK") {
                    console.error("Network error joining soundboard:", err);
                    setAnnouncement("Network error connecting to Libretto. Please check your connection and refresh the page.");
                    setShowAnnouncement(true);
                }
            }
        }
        joinSoundboard();
    }, [studioData.token, studioData.roomName]); // Only depend on token and roomName

    const guestURL = (roomName, userFriendlyRoomName) => {
        return CLIENT_URL + "/guest/" + roomName + "/" + urlFriendlyUserFriendlyRoomName({userFriendlyRoomName});
    }

    const onLeave = async () => {
        if (recordingInProgress) {
            await stopRecording();
        }
        try {
            await room?.disconnect?.(true);
        } catch (error) {
            console.error('[NewStudio] Failed to disconnect room during leave', error);
        }
        navigate("/dashboard")
        setTimeout(() => {
            window.location.reload();
        }, 100);
    }

    const onDisconnected = (reason) => {
        // Log disconnection reason for debugging
        console.error("LiveKit room disconnected!", reason);
        console.log("Disconnection details:", {
            reason,
            wasRecording: recordingInProgress,
            roomName: studioData.roomName,
            timestamp: new Date().toISOString(),
        });
    }

    const recordingTitle = userChoices.title ? userChoices.title : 'My podcast episode';

    const titleBoxStyle = {
        display: isSmd ? 'flex' : 'none',
    }

    const countdownBoxStyle = {
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: isDarkUi ? "rgba(18, 20, 24, 0.78)" : "rgba(255,255,255,0.88)",
        backdropFilter: "blur(4px)",
        zIndex: 1000,
    }

    const textStyle = {
        fontFamily: "Inter",
        fontSize: {md: "9px", lg: "11px", xl: "14px"},
        fontStyle: "normal",
        fontWeight: {md: "350", lg: "400", xl: "500"},
        lineHeight: {md: "13px", lg: "15px", xl: "20px"},
        letterSpacing: {md: "0.1px", lg: "0.1px", xl: "0.14px"},
        color: "var(--app-text-primary)",
        // disable highlight on double click
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        KhtmlUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none",
    }

    const countdownTextStyle = {
        color: "var(--app-text-primary)",
        fontSize: "96px",
        fontWeight: 600,
        fontFamily: "Inter, sans-serif",
        lineHeight: 1,
    }
    
    const countdownLabelStyle = {
        color: "var(--app-text-muted)",
        fontSize: "12px",
        fontWeight: 500,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        marginTop: "16px",
    }

    const [showRecordingGuidelines, setShowRecordingGuidelines] = useState(false);

    const topAreaStyle = (videoAreaFullScreen) => {
        return ({
            display: videoAreaFullScreen ? 'none' : 'flex',
            marginTop: "5px",
        })
    }

    const infoIconStyle = {
        fontSize: '16px',
        marginLeft: '4px',
        verticalAlign: 'text-bottom',
        cursor: 'pointer',
        color: 'var(--app-text-muted)',
    };

    const userPlan = auth.getUserPlan();

    const onInfoClick = () => {
        setShowRecordingGuidelines(true);
    }

    const {openDialog, ConfirmDialogComponent} = useConfirmDialog({confirmText: 'Leave', cancelText: 'Stay'});

    const handleLeaveWhileRecording = () => {
        openDialog({
            title: 'Leave studio while recording in progress?',
            message: 'Please stop the recording and wait for local recording to upload before leaving.',
            onConfirm: async () => {
                await onLeave();
            },
        });
    }

    const handleLeave = () => {
        if (recordingInProgress) {
            handleLeaveWhileRecording();
        } else {
            onLeave();
        }
    }

    const [isScreenShareEnabled, setIsScreenShareEnabled] = React.useState(false);

    const onScreenShareChange = React.useCallback(
        (enabled) => {
            setIsScreenShareEnabled(enabled);
        },
        [setIsScreenShareEnabled],
    );

    useEffect(() => {
        if (screenShareInitiallyEnabled) {
            // Delay to ensure ref is populated and component is fully mounted
            setTimeout(() => {
                shareScreenButtonRef.current?.click();
            }, 1000);
        }
    }, []);

    const persistentUserChoices = usePersistentUserChoices({preventSave: false});

    const {
        saveAudioInputEnabled,
        saveVideoInputEnabled,
        saveAudioInputDeviceId,
        saveVideoInputDeviceId,
    } = persistentUserChoices;

    const saveAudioOutputDeviceId = persistentUserChoices?.saveAudioOutputDeviceId;

    const lobbyAudioInputDeviceId = userChoices?.audioDeviceId || '';
    const lobbyVideoInputDeviceId = userChoices?.videoDeviceId || '';
    const lobbyAudioOutputDeviceId = userChoices?.outputDeviceId || '';

    const preferredAudioInputDeviceId = useMemo(() => {
        if (lobbyAudioInputDeviceId && lobbyAudioInputDeviceId !== 'none') {
            return lobbyAudioInputDeviceId;
        }
        return getPreferredAudioInputDeviceId();
    }, [lobbyAudioInputDeviceId]);

    const preferredVideoInputDeviceId = useMemo(() => {
        if (lobbyVideoInputDeviceId && lobbyVideoInputDeviceId !== 'none') {
            return lobbyVideoInputDeviceId;
        }
        return getPreferredVideoInputDeviceId();
    }, [lobbyVideoInputDeviceId]);

    const preferredAudioOutputDeviceId = useMemo(() => {
        if (lobbyAudioOutputDeviceId) {
            return lobbyAudioOutputDeviceId;
        }
        return getPreferredAudioOutputDeviceId() || 'default';
    }, [lobbyAudioOutputDeviceId]);

    useEffect(() => {
        if (preferredAudioInputDeviceId) {
            setPreferredAudioInputDeviceId(preferredAudioInputDeviceId);
        }
    }, [preferredAudioInputDeviceId]);

    useEffect(() => {
        if (preferredVideoInputDeviceId) {
            setPreferredVideoInputDeviceId(preferredVideoInputDeviceId);
        }
    }, [preferredVideoInputDeviceId]);

    useEffect(() => {
        if (preferredAudioOutputDeviceId) {
            setPreferredAudioOutputDeviceId(preferredAudioOutputDeviceId);
        }
    }, [preferredAudioOutputDeviceId]);

    const handleSaveAudioInputDeviceId = useCallback((deviceId) => {
        saveAudioInputDeviceId(deviceId);
        setPreferredAudioInputDeviceId(deviceId);
    }, [saveAudioInputDeviceId]);

    const handleSaveVideoInputDeviceId = useCallback((deviceId) => {
        saveVideoInputDeviceId(deviceId);
        setPreferredVideoInputDeviceId(deviceId);
    }, [saveVideoInputDeviceId]);

    const handleSaveAudioOutputDeviceId = useCallback((deviceId) => {
        saveAudioOutputDeviceId?.(deviceId);
        setPreferredAudioOutputDeviceId(deviceId);
    }, [saveAudioOutputDeviceId]);

    const microphoneOnChange = React.useCallback(
        (enabled, isUserInitiated) =>
            isUserInitiated ? saveAudioInputEnabled(enabled) : null,
        [saveAudioInputEnabled],
    );

    const cameraOnChange = React.useCallback(
        (enabled, isUserInitiated) =>
            isUserInitiated ? saveVideoInputEnabled(enabled) : null,
        [saveVideoInputEnabled],
    );

    const [sidebarItemSelected, setSidebarItemSelected] = useState("");
    const previousLocalRecordingErrorsRef = useRef(new Map());

    // Auto-open People sidebar when any participant reports a new local recording failure.
    useEffect(() => {
        const previousMap = previousLocalRecordingErrorsRef.current;
        const participantIds = new Set(participants.map((p) => p.id));

        // Remove participants that left
        for (const id of Array.from(previousMap.keys())) {
            if (!participantIds.has(id)) previousMap.delete(id);
        }

        let hasNewError = false;
        for (const participant of participants) {
            const currentCode = (participant.localRecordingError || '').trim();
            const previousCode = (previousMap.get(participant.id) || '').trim();

            if (currentCode !== previousCode) {
                if (previousCode === '' && currentCode !== '') {
                    hasNewError = true;
                }
                previousMap.set(participant.id, currentCode);
            }
        }

        if (hasNewError && sidebarItemSelected !== StudioSidebarItems.PEOPLE) {
            setSidebarItemSelected(StudioSidebarItems.PEOPLE);
        }
    }, [participants, sidebarItemSelected]);

    const handleSendInvite = async (email) => {
        try {
            await fetchContext.authAxios.post(
                `/invite`,
                { recipientEmail: email, eventTime: Math.floor(Date.now() / 1000) },
                { headers: { Authorization: `Bearer ${auth.getToken()}` } }
            );
            mixpanel.track('Invite_Sent_From_Studio', { roomName: studioData.roomName });
        } catch (err) {
            console.error('Failed to send invite:', err);
            throw err;
        }
    }

    const handleInviteClick = () => {
        if (sidebarItemSelected === StudioSidebarItems.INVITE) {
            setSidebarItemSelected("");
            return;
        }
        setSidebarItemSelected(StudioSidebarItems.INVITE);
    }


    const updateRecordingMode = async (mode) => {
        if (studioData.roomName === '') {
            return;
        }

        mode = AdaptRecordingMode(mode);

        const entityRef = doc(db, kRoomStateCollectionName, studioData.roomName);
        try {
            await updateDoc(entityRef, {recordingType: mode});
        } catch (error) {
            console.error('Error updating recording type for room: ', error);
        }
    }


    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                background: 'var(--app-bg-canvas)',
                gap: '8px',
                height: '100vh',
                width: '100vw',
                overflow: 'hidden',
            }}
        >
            <ContextMenu/>
            
            {/* Guest Admission Modal - shown when guests are waiting to join */}
            {!isSoloRecording && showAdmissionModal && pendingGuests.length > 0 && (
                <GuestAdmissionModal
                    pendingGuests={pendingGuests}
                    onApprove={handleApproveGuest}
                    onDeny={handleDenyGuest}
                    onClose={() => setShowAdmissionModal(false)}
                    isProcessing={isProcessingAdmission}
                />
            )}

            {/* Top bar (aspect ratio button removed from here) */}
            <Box sx={{
                height: '40px',
                width: '100%',
                paddingY: "8px",
                paddingLeft: "24px",
                paddingRight: "10px",
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between'
            }}>
                <Box sx={{display: 'flex'}}>
                    <V2EditorBackButton handleClick={navigateToHome} disabled={false}
                                        theme={isDarkUi ? LibrettoStudioTheme.Dark : LibrettoStudioTheme.Light}/>
                </Box>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                    <Box sx={{display: isSoloRecording ? 'none' : 'flex', alignItems: 'center', gap: 1}}>
                        <StudioInviteButton 
                            onClick={handleInviteClick}
                            disabled={false}
                        />
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flex: 1,
                    gap: '8px',
                    minHeight: 0,
                    position: 'relative', // For absolute positioned sidebar
                    px: '8px',
                    pb: '8px',
                }}
            >
                {/* Left area: takes remaining width */}
                <Box
                    sx={{
                        background: 'var(--app-bg-subtle)',
                        display: 'flex',
                        flex: '1 1 0',
                        minWidth: 0,
                        border: "1px solid var(--app-border-subtle)",
                        borderRadius: "14px",
                        boxShadow: isDarkUi ? '0 10px 24px rgba(0, 0, 0, 0.35)' : '0 6px 20px rgba(15, 23, 42, 0.04)',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                    }}
                >
                    {/* Main Studio Content - inlined to prevent re-renders */}
                    {/*<Dialog*/}
                    {/*    open={isTooSmall}*/}
                    {/*    onClose={() => {}}*/}
                    {/*    aria-labelledby="form-dialog-title"*/}
                    {/*>*/}
                    {/*    <DialogContent sx={{*/}
                    {/*        borderRadius: '30px',*/}
                    {/*        display: 'flex',*/}
                    {/*        justifyContent: 'center',*/}
                    {/*        alignItems: 'center',*/}
                    {/*        minWidth: {xs: '300px', md: '400px'},*/}
                    {/*    }}>*/}
                    {/*        <Typography><b>Please switch to a larger screen.</b> For now, you need a larger screen here.</Typography>*/}
                    {/*    </DialogContent>*/}
                    {/*</Dialog>*/}
                    
                    <Grid container direction="column" style={pageStyle(studioTheme, showAnnouncement)} justifyContent="flex-start" alignItems="center">
                        {/* Invite Participant Side Panel */}
                        {inviteCardOpen && (
                            <NewInviteParticipantCard
                                inviteLink={inviteLink}
                                invitedEmails={invitedEmails}
                                onClose={() => setInviteCardOpen(false)}
                                roomName={studioData.roomName}
                            />
                        )}

                        {/* Recording Guidelines Dialog */}
                        <Dialog
                            open={showRecordingGuidelines}
                            onClose={() => setShowRecordingGuidelines(false)}
                            aria-labelledby="form-dialog-title"
                            maxWidth={"500px"}
                        >
                            <DialogContent sx={{
                                borderRadius: '30px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: "0px 0px 0px 0px",
                            }}>
                                <RecordingGuidelinesCard/>
                            </DialogContent>
                        </Dialog>

                        {/* Recording stopped overlay with participant progress */}
                        {localRecordingStopped && showRecordingEndedCard && (
                            <HostRecordingStoppedOverlay
                                participants={participants.map(p => ({
                                    id: p.id,
                                    name: p.isHost ? (userChoices.username || 'You (Host)') : p.name,
                                    // For host, use local uploadProgress state; for guests, use Firestore value
                                    uploadProgress: p.isHost ? effectiveUploadProgress : (p.uploadProgress ?? 0),
                                    isComplete: p.uploadComplete ?? false,
                                    isHost: p.isHost,
                                }))}
                                onClose={() => setShowRecordingEndedCard(false)}
                                onNavigate={handleNavigateToRecording}
                                isHost={true}
                            />
                        )}
                        
                        {/* Mini indicator to re-open overlay when closed */}
                        {localRecordingStopped && !showRecordingEndedCard && (
                            <UploadStatusIndicator
                                isUploading={!hostUploadCompleted || !participantsLocalRecordingDone}
                                isComplete={hostUploadCompleted && participantsLocalRecordingDone}
                                onClick={() => setShowRecordingEndedCard(true)}
                            />
                        )}
                        
                        <Grid item sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                            <Box style={videoDisplayStyle(videoAreaFullScreen)}>
                                            <LiveKitRoom
                                                serverUrl={serverUrl} 
                                                token={studioData.token}
                                                data-lk-theme="default"
                                                video={userChoices.videoEnabled} 
                                                audio={userChoices.audioEnabled}
                                                style={{height: '100%', width: '100%', borderRadius: '0px'}}
                                                connect={true} 
                                                onDisconnected={onDisconnected}
                                            >
                                {recordingMode === LibrettoRecordingMode.Audio ? (
                                    <NewStudioVideoConference
                                        localRecordingEnabled={localRecordingEnabled}
                                        assetIdRef={assetIdRef}
                                        setRecordingDisabled={setRecordingDisabled}
                                        backgroundImage={backgroundImage}
                                        highQualityRecordingRef={highQualityRecordingRef}
                                        roomName={studioData.roomName}
                                        recordingInProgress={recordingInProgress}
                                        startRecording={startRecording}
                                        videoMirrored={videoMirrored}
                                        studioNameStyle={StudioNameStyleString(studioNameStyle)}
                                        stopRecording={stopRecording}
                                        enableKrispNoiseCancellation={noiseCancellationEnabled}
                                        localRecordingInProgress={localRecordingInProgress}
                                        livekitToken={studioData.token}
                                        hostIdentity={studioData.hostIdentity}
                                        brandLogoUrl={selectedBrandLogo.url}
                                        setLocalRecordingStopped={setLocalRecordingStopped}
                                        videoResolution={selectedVideoResolution}
                                        setUploadProgress={setUploadProgress}
                                        themeBackgroundColor={studioThemeToBackgroundColor(studioTheme)}
                                        setRoom={setRoom} 
                                        isSoloSession={isSoloRecording}
                                    />
                                ) : (
	                                    <NewStudioVideoConferenceVideoRecording
	                                        setUploadProgress={setUploadProgress}
	                                        setScreenShareUploadProgress={setScreenShareUploadProgress}
	                                        setRecordingDisabled={setRecordingDisabled}
	                                        themeBackgroundColor={studioThemeToBackgroundColor(studioTheme)}
	                                        assetIdRef={assetIdRef}
	                                        screenRecordingLocalTrackId={hostParticipant?.screenRecordingLocalTrackId || null}
                                        getRecordingElapsedMs={getRecordingElapsedMs}
                                        localRecordingEnabled={localRecordingEnabled}
                                        backgroundImage={backgroundImage}
                                        highQualityRecordingRef={highQualityRecordingRef}
                                        videoMirrored={videoMirrored}
                                        roomName={studioData.roomName}
                                        recordingInProgress={recordingInProgress}
                                        startRecording={startRecording}
                                        studioNameStyle={StudioNameStyleString(studioNameStyle)}
                                        stopRecording={stopRecording}
                                        enableKrispNoiseCancellation={noiseCancellationEnabled}
                                        localRecordingInProgress={localRecordingInProgress}
                                        livekitToken={studioData.token}
                                        hostIdentity={studioData.hostIdentity}
                                        brandLogoUrl={selectedBrandLogo.url}
                                        setLocalRecordingStopped={setLocalRecordingStopped}
                                        videoResolution={selectedVideoResolution}
                                        setRoom={setRoom} 
                                        isSoloSession={isSoloRecording}
                                    />
                                )}
                            </LiveKitRoom>
                            </Box>
                        </Grid>
                        
                        <Grid item alignSelf="center">
                            {countdown !== null && countdown > 0 && (
                                <Box style={countdownBoxStyle}>
                                    <Box 
                                        key={countdown}
                                        sx={{
                                            ...countdownTextStyle,
                                            animation: 'countdownPop 0.3s ease-out',
                                            '@keyframes countdownPop': {
                                                '0%': { opacity: 0, transform: 'scale(1.05)' },
                                                '100%': { opacity: 1, transform: 'scale(1)' },
                                            },
                                        }}
                                    >
                                        {countdown}
                                    </Box>
                                    <Box style={countdownLabelStyle}>Recording starts in…</Box>
                                </Box>
                            )}
                        </Grid>
                        
                        <Grid container item justifyContent="center" alignItems="center"
                              display={isTooSmall ? "none" : "inline-flex"} mb={0} mt={0}>
                            <Grid container item justifyContent="center" alignItems="center" display="flex"
                                  sx={{
                                      backgroundColor: 'var(--app-bg-surface)',
                                      borderTop: '1px solid var(--app-border-subtle)',
                                      borderRadius: '0 0 14px 14px',
                                      pt: 1.5,
                                      pb: 1.75,
                                      px: 2.5,
                                  }}>
                                <Grid item style={soundBoardStyle(videoAreaFullScreen)}>
                                    <Box display="flex" gap={1.75} alignItems="center">
                                        {/* Leave Button */}
                                        <LeaveCallButton onClick={handleLeave} />

                                        {/* Audio Button (Mic + Speaker) */}
                                        <SimpleAudioButton
                                            room={room}
                                            isMicEnabled={true}
                                            selectDisabled={false}
                                            saveAudioInputDevice={handleSaveAudioInputDeviceId}
                                            saveAudioOutputDevice={handleSaveAudioOutputDeviceId}
                                            initialAudioInputDeviceId={preferredAudioInputDeviceId}
                                            initialAudioOutputDeviceId={preferredAudioOutputDeviceId}
                                        />

                                        {/* Video Button (Camera + Resolution) */}
                                        <SimpleVideoButton
                                            room={room}
                                            isCamEnabled={true}
                                            selectDisabled={false}
                                            saveVideoInputDevice={handleSaveVideoInputDeviceId}
                                            initialVideoInputDeviceId={preferredVideoInputDeviceId}
                                            selectedResolution={hostVideoResolution}
                                            onResolutionChange={setHostVideoResolution}
                                        />

                                        <SimpleScreenShareButton
                                            ref={shareScreenButtonRef}
                                            room={room}
                                            onChange={onScreenShareChange}
                                        />

                                        {/*<Tooltip title="Teleprompter">*/}
                                        {/*    <Box*/}
                                        {/*        onClick={() => setTeleprompterOpen((prev) => !prev)}*/}
                                        {/*        sx={{*/}
                                        {/*            ...smallButtonStyle,*/}
                                        {/*            backgroundColor: teleprompterOpen ? 'rgba(0,0,0,0.04)' : '#ffffff',*/}
                                        {/*        }}*/}
                                        {/*    >*/}
                                        {/*        <NotesOutlinedIcon sx={{ fontSize: "18px", color: teleprompterOpen ? "#1F2937" : "#6B7280" }} />*/}
                                        {/*    </Box>*/}
                                        {/*</Tooltip>*/}

                                        {/* Record Button with Mode Selector */}
                                        <Box sx={{ ml: 1.5 }}>
                                            <RecordButtonWithModeSelector
                                                recordingInProgress={recordingInProgress}
                                                localRecordingInProgress={localRecordingInProgress}
                                                onStartRecording={async () => {
                                                    setRecordingStarting(true);
                                                    await startRecording();
                                                }}
                                                onStopRecording={stopRecording}
                                                recordingMode={recordingMode === LibrettoRecordingMode.Audio ? 'audio' : 'video'}
                                                onRecordingModeChange={async (mode) => {
                                                    const newMode = mode === 'audio' ? LibrettoRecordingMode.Audio : LibrettoRecordingMode.Video;
                                                    await updateRecordingMode(newMode);
                                                }}
                                                disabled={isSoloRecording && recordingDisabled}
                                            />
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
                {/* Sidebars - overlay positioned (for PEOPLE and CHAT) */}
                {sidebarItemSelected && sidebarItemSelected !== StudioSidebarItems.INVITE && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        right: 100, // Account for the sidebar rail width
                        bottom: 0,
                        width: '300px',
                        zIndex: 10,
                        transition: 'all 0.2s ease',
                        background: 'var(--app-bg-surface)',
                        border: "0.5px solid var(--app-border-subtle)",
                        borderRadius: "12px",
                        overflow: 'hidden',
                        boxShadow: isDarkUi ? '0 8px 24px rgba(0, 0, 0, 0.4)' : '0 4px 20px rgba(0, 0, 0, 0.08)',
                    }}>
                        {sidebarItemSelected === StudioSidebarItems.PEOPLE && (
                            <StudioPeopleSidebar 
                                participants={participants}
                                room={room}
                                recordingInProgress={recordingInProgress}
                                recordingStopped={localRecordingStopped}
                                hostName={userChoices.username || 'You (Host)'}
                                hostConnectionQuality={mapLiveKitConnectionQuality(room?.localParticipant?.connectionQuality ?? 'good')}
                                hostVideoResolution={hostVideoResolution}
                                hostUploadProgress={effectiveUploadProgress}
                                hostUploadComplete={hostUploadCompleted}
                                hostLocalRecordingError={hostLocalRecordingError}
                                hostNoiseCancellationEnabled={noiseCancellationEnabled}
                                onToggleNoiseCancellation={handleToggleNoiseCancellation}
                                onToggleHostNoiseCancellation={handleToggleHostNoiseCancellation}
                            />
                        )}
                        {sidebarItemSelected === StudioSidebarItems.CHAT && (
                            <StudioChatSidebar room={room} />
                        )}
                    </Box>
                )}
                
                {/* Invite panel - positioned near invite button with click-away */}
                {sidebarItemSelected === StudioSidebarItems.INVITE && (
                    <ClickAwayListener onClickAway={() => setSidebarItemSelected("")}>
                        <Box sx={{
                            position: 'fixed',
                            top: '56px', // Below top bar (40px height + 8px padding + 8px offset)
                            right: '120px', // Aligned near invite button and session rail
                            width: '320px',
                            maxHeight: '70vh',
                            zIndex: 1000,
                            background: 'var(--app-bg-surface)',
                            border: "0.5px solid var(--app-border-subtle)",
                            borderRadius: "12px",
                            overflow: 'hidden',
                            boxShadow: isDarkUi ? '0 12px 36px rgba(0, 0, 0, 0.45)' : '0 8px 32px rgba(0, 0, 0, 0.12)',
                        }}>
                            <StudioInviteSidebar inviteLink={inviteLink} onSendInvite={handleSendInvite} />
                        </Box>
                    </ClickAwayListener>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        width: '92px',
                        flexShrink: 0,
                        flexDirection: 'column',
                        padding: '10px 8px 12px',
                        gap: '10px',
                        borderRadius: '14px',
                        border: '1px solid var(--app-border-subtle)',
                        backgroundColor: 'var(--app-bg-surface)',
                        boxShadow: isDarkUi ? '0 8px 18px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(15, 23, 42, 0.04)',
                    }}
                >
                    <Typography
                        sx={{
                            color: 'var(--app-text-muted)',
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '10px',
                            fontWeight: 600,
                            lineHeight: '12px',
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            width: '100%',
                            textAlign: 'center',
                            userSelect: 'none',
                        }}
                    >
                        Session
                    </Typography>
                    <V2StudioSidebar setSidebarItemSelected={setSidebarItemSelected}
                                     sidebarItemSelected={sidebarItemSelected}/>
                </Box>
            </Box>
            <TeleprompterWindow
                open={teleprompterOpen}
                onClose={() => setTeleprompterOpen(false)}
                recordingInProgress={recordingInProgress}
            />
        </Box>
    )
}

export default NewStudio;
